import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Track a page view for SEO pages. Uses upsert to increment daily counter.
 * Fire-and-forget: errors are silently ignored to avoid impacting UX.
 */
export function useSeoPageView(
  pageType: "movie" | "series" | "football" | "ufc",
  pageSlug: string | undefined,
  pageTitle?: string
) {
  useEffect(() => {
    if (!pageSlug) return;

    const today = new Date().toISOString().split("T")[0];

    // Try upsert: increment if exists, insert if not
    const trackView = async () => {
      try {
        // First try to find existing
        const { data: existing } = await supabase
          .from("seo_page_views")
          .select("id, views_count")
          .eq("page_type", pageType)
          .eq("page_slug", pageSlug)
          .eq("view_date", today)
          .maybeSingle();

        if (existing) {
          await supabase
            .from("seo_page_views")
            .update({ views_count: existing.views_count + 1, updated_at: new Date().toISOString() })
            .eq("id", existing.id);
        } else {
          await supabase.from("seo_page_views").insert({
            page_type: pageType,
            page_slug: pageSlug,
            page_title: pageTitle || null,
            view_date: today,
            views_count: 1,
          });
        }
      } catch {
        // Silent fail - tracking should never break UX
      }
    };

    trackView();
  }, [pageType, pageSlug, pageTitle]);
}
